import { gql } from '@apollo/client';
import {
    GROUP_FRAGMENT,
    GroupFragment,
    RetainedView,
    SAVED_LAYOUT_FRAGMENT,
    SavedLayoutFragment,
    USER_SETTINGS_FRAGMENT,
    UserSettingsFragment,
} from 'api/fragments';
import graphQlClient from './graphQlClient';
import _ from 'lodash';
import { IGroupSettings } from '_legacy/components/admin/groups/GroupSettings';

export module UserApi {
    export interface UserInfoResult {
        id: string;
        userId: number;
        groups: GroupFragment[];
        permissions: { groupId: string; canWrite: boolean }[];
        userLayouts: SavedLayoutFragment[];
        sharedLayouts: SavedLayoutFragment[];
        uiSettings: UserSettingsFragment;
        groupsSettings: [{ groupSettings: IGroupSettings; groupId: string }];
    }

    const USER_INFO_QUERY = gql`
        ${GROUP_FRAGMENT}
        ${SAVED_LAYOUT_FRAGMENT}
        ${USER_SETTINGS_FRAGMENT}

        query UserInfo {
            me {
                id
                userId
                groups {
                    ...GroupFragment
                }
                permissions {
                    groupId
                    canWrite
                }
                userLayouts {
                    ...SavedLayoutFragment
                }
                sharedLayouts {
                    ...SavedLayoutFragment
                }
                uiSettings {
                    ...UserSettingsFragment
                }
                groupsSettings {
                    groupSettings {
                        useDeliveryZoneForAreaMapping
                        useDeliveryZoneForAreaGearedMapping
                        useDeliveryZoneForAreaGearlessMapping
                        useDeliveryZoneForDirectionMapping
                        useHyphenSeparatorForLocations
                        tankersGroupType
                    }
                    groupId
                }
            }
        }
    `;

    export async function fetchUserInfo(): Promise<UserInfoResult> {
        const result = await graphQlClient().query<{ me: UserInfoResult }>({
            query: USER_INFO_QUERY,
            fetchPolicy: 'network-only',
        });
        return result.data.me;
    }

    const UPDATE_USER_SETTINGS_MUTATION = gql`
        ${USER_SETTINGS_FRAGMENT}

        mutation ChangeLayoutMenuSettings(
            $userExpanded: Boolean!
            $adminExpanded: Boolean!
            $templateExpanded: Boolean!
        ) {
            changeLayoutMenuSettings(
                userExpanded: $userExpanded
                adminExpanded: $adminExpanded
                templateExpanded: $templateExpanded
            ) {
                ...UserSettingsFragment
            }
        }
    `;

    const UPDATE_RETAINED_APP_STATE = gql`
        ${USER_SETTINGS_FRAGMENT}

        mutation changeRetainedAppState(
            $retainedView: String!
            $retainedGroup: String!
        ) {
            changeRetainedAppState(
                retainedView: $retainedView
                retainedGroup: $retainedGroup
            ) {
                ...UserSettingsFragment
            }
        }
    `;

    export async function updateUserSettings(
        userExpanded: boolean,
        adminExpanded: boolean,
        templateExpanded: boolean
    ): Promise<
        Pick<
            UserSettingsFragment,
            | 'layoutsUserSectionExpanded'
            | 'layoutsAdminTemplateSectionExpanded'
            | 'layoutsTemplateSectionExpanded'
        >
    > {
        const result = await graphQlClient().mutate<{
            changeLayoutMenuSettings: UserSettingsFragment;
        }>({
            mutation: UPDATE_USER_SETTINGS_MUTATION,
            variables: {
                userExpanded,
                adminExpanded,
                templateExpanded,
            },
        });
        if (!result.data) {
            throw new Error('Failed to update user settings');
        }

        return _.pick(result.data.changeLayoutMenuSettings, [
            'layoutsUserSectionExpanded',
            'layoutsAdminTemplateSectionExpanded',
            'layoutsTemplateSectionExpanded',
        ]);
    }

    export async function updateRetainedAppState(
        retainedView: RetainedView,
        retainedGroup: string
    ): Promise<void> {
        const result = await graphQlClient().mutate<{
            changeRetainedAppState: UserSettingsFragment;
        }>({
            mutation: UPDATE_RETAINED_APP_STATE,
            variables: {
                retainedView: retainedView,
                retainedGroup: retainedGroup,
            },
        });
        if (!result.data) {
            throw new Error('Failed to update retained App State');
        }

        return;
    }

    const CHANGE_ZONE_MAPPING_CONFIG_MUTATION = gql`
        ${USER_SETTINGS_FRAGMENT}

        mutation changeZoneMappingConfig($zoneMappingConfigId: ID) {
            changeZoneMappingConfig(zoneMappingConfigId: $zoneMappingConfigId) {
                ...UserSettingsFragment
            }
        }
    `;

    export async function changeZoneMappingConfig(
        zoneMappingConfigId: string | null
    ): Promise<UserSettingsFragment> {
        const result = await graphQlClient().mutate<{
            changeZoneMappingConfig: UserSettingsFragment;
        }>({
            mutation: CHANGE_ZONE_MAPPING_CONFIG_MUTATION,
            variables: { zoneMappingConfigId },
        });
        if (!result.data) {
            throw new Error('Failed to update zone mapping config');
        }
        return result.data.changeZoneMappingConfig;
    }

    const CHANGE_DEFAULT_CARGO_MUTATION = gql`
        ${USER_SETTINGS_FRAGMENT}

        mutation changeDefaultCargo($cargoId: String) {
            changeDefaultCargo(cargoId: $cargoId) {
                ...UserSettingsFragment
            }
        }
    `;

    export async function changeDefaultCargo(
        cargoId: string | null
    ): Promise<UserSettingsFragment> {
        const result = await graphQlClient().mutate<{
            changeDefaultCargo: UserSettingsFragment;
        }>({
            mutation: CHANGE_DEFAULT_CARGO_MUTATION,
            variables: { cargoId },
        });
        if (!result.data) {
            throw new Error('Failed to update default cargo');
        }
        return result.data.changeDefaultCargo;
    }

    export interface ZoneMappingConfigOption {
        id: string;
        name: string;
        mappings: { key: string; value: string }[];
        dischMappings?: { key: string; value: string }[];
    }

    const ZONE_MAPPING_CONFIGS_QUERY = gql`
        query ZoneMappingConfigs($datasetId: Int!) {
            zoneMappingConfigs(datasetId: $datasetId) {
                id
                name
                mappings {
                    key
                    value
                }
                dischMappings {
                    key
                    value
                }
            }
        }
    `;

    export async function fetchZoneMappingConfigs(
        datasetId: number
    ): Promise<ZoneMappingConfigOption[]> {
        const result = await graphQlClient().query<{
            zoneMappingConfigs: ZoneMappingConfigOption[];
        }>({
            query: ZONE_MAPPING_CONFIGS_QUERY,
            variables: { datasetId },
        });
        return result.data.zoneMappingConfigs;
    }
}
